import React from 'react';
import { reportError } from '../lib/diagnostics';

// Error boundary to recover gracefully from runtime errors
// (especially DOM mutation errors caused by browser extensions
// like Google Translate, Grammarly, etc.)
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this._reloadTimer = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('YNX ErrorBoundary caught:', error, info);
    // Forward to the diagnostics pipeline so Carl can review it in the admin.
    try {
      reportError({
        source: 'frontend',
        severity: 'critical',
        error_type: error?.name || 'ReactBoundary',
        message: error?.message || String(error),
        stack: (error?.stack || '') + '\n\nComponentStack:\n' + (info?.componentStack || ''),
      });
    } catch (err) {
      // Fail-silent BY DESIGN — if the diagnostics reporter itself throws
      // inside componentDidCatch, we MUST NOT cascade the error or React
      // will mount a recovery loop. Debug-only logging.
      console.debug('YNX ErrorBoundary: reportError failed silently', err);
    }
    // Attempt auto-recovery after 800ms
    this._reloadTimer = setTimeout(() => {
      this.setState({ hasError: false, error: null });
    }, 800);
  }

  componentWillUnmount() {
    if (this._reloadTimer) clearTimeout(this._reloadTimer);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          position: 'fixed', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 16,
          background: '#020509', color: '#b8d4f0',
          fontFamily: "'Share Tech Mono', monospace",
          padding: 24, textAlign: 'center', zIndex: 9999,
        }}>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: 28, letterSpacing: 6, color: '#e8f4ff', textShadow: '0 0 20px rgba(0,229,255,0.5)' }}>
            YNX · RECOVERY MODE
          </div>
          <div style={{ fontSize: 13, opacity: 0.7, maxWidth: 520, lineHeight: 1.6 }}>
            Une erreur de rendu a été interceptée. L'interface va se rétablir automatiquement…
            Si le problème persiste, désactivez les extensions de traduction du navigateur (Google Translate, etc.) et rechargez.
          </div>
          <button
            onClick={this.handleReload}
            style={{
              background: 'transparent',
              border: '1px solid rgba(0,229,255,0.4)',
              color: '#00e5ff',
              padding: '8px 20px',
              borderRadius: 6,
              cursor: 'pointer',
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: 12,
              letterSpacing: 2,
            }}
          >
            RECHARGER MAINTENANT
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
